import { FC } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import MetricsTable, { CHANNEL_METRICS, ExtraColumn, categoryColumn } from './MetricsTable';
import DeviceFunnel from './DeviceFunnel';
import type {
  MatrixCell,
  WebinarGroupedEvaluationDetailData,
} from '../../helpers/webinarStatisticsData';

const HEAT_UP_ORDER = ['1', '2', '3', '4+', 'Ohne Nr.'];

/**
 * Reihenfolge der Kohorten „Nach Anmeldezeitpunkt" — dieselben Etiketten wie
 * `node-app/src/helpers/webinarEvaluation.ts` (`SIGNUP_LEAD_BUCKETS`, `SIGNUP_LEAD_UNSURE`). Unbekannte
 * Kategorien (Anfos ohne Anmeldung) stehen danach.
 */
const SIGNUP_LEAD_ORDER = [
  'mehr als 3 Tage vorher',
  '1–3 Tage vorher',
  '6–24 h vorher',
  '1–6 h vorher',
  'letzte Stunde vorher',
  'bis 2 h nach Start',
  '2–24 h nach Start',
  'später als 24 h nach Start',
  'Anmeldezeit unsicher (vor Mai 2026)',
];
const leadRank = (c: string) => {
  const i = SIGNUP_LEAD_ORDER.indexOf(c);
  return i < 0 ? SIGNUP_LEAD_ORDER.length : i;
};

const Section: FC<{ title: string; text?: string; children: React.ReactNode }> = ({
  title,
  text,
  children,
}) => (
  <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
    <Typography variant="h6" sx={{ color: 'black', mb: text ? 0.5 : 1.5 }}>
      {title}
    </Typography>
    {text && (
      <Typography variant="body2" sx={{ color: '#555', mb: 1.5 }}>
        {text}
      </Typography>
    )}
    {children}
  </Paper>
);

const matrixLeading: ExtraColumn<MatrixCell>[] = [
  { id: 'ad_type', label: 'Ad-Type', sortValue: (r) => r.ad_type, render: (r) => r.ad_type },
  { id: 'blog', label: 'Blog', sortValue: (r) => r.blog, render: (r) => r.blog },
];

/**
 * Alle Kanal-Gruppierungen — geteilt von Detailseite (Tab „Gruppierung“) und Uebersicht (Tab
 * „Gruppierte Auswertungen“). Jede Person steht in genau einer Zeile (Kanal ihrer ersten
 * Anmeldung), die Zeilen summieren also auf die Summenzeile.
 */
const GroupedSections: FC<{ data: WebinarGroupedEvaluationDetailData }> = ({ data }) => {
  const all = data.totals.all;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Section
        title="Nach Ad-Type (Akquisekanal)"
        text="Über welchen Werbeweg die Personen kamen. Anfos ohne Anmeldung stehen in eigenen Zeilen am Ende."
      >
        <MetricsTable
          rows={data.byAdType}
          leading={[categoryColumn('Ad-Type')]}
          metrics={CHANNEL_METRICS}
          totals={all}
          rowKey={(r) => r.category}
          defaultSortBy="registrations"
        />
      </Section>

      <Section title="Nach Blog (Marke der Quelle)">
        <MetricsTable
          rows={data.byBlog}
          leading={[categoryColumn('Blog')]}
          metrics={CHANNEL_METRICS}
          totals={all}
          rowKey={(r) => r.category}
          defaultSortBy="registrations"
        />
      </Section>

      {data.byHeatUpNummer.length > 0 && (
        <Section
          title="Nach Heat-Up-Nummer"
          text="Nur Personen, die über ein Heat-Up-Mailing kamen. Summenzeile = alle Heat-Up-Personen."
        >
          <MetricsTable
            rows={[...data.byHeatUpNummer].sort(
              (a, b) => HEAT_UP_ORDER.indexOf(a.category) - HEAT_UP_ORDER.indexOf(b.category),
            )}
            leading={[
              {
                ...categoryColumn('Heat-Up Nr.'),
                sortValue: (r) => HEAT_UP_ORDER.indexOf(r.category),
              },
            ]}
            metrics={CHANNEL_METRICS}
            totals={data.totals.heatUp}
            totalsLabel="Heat-Up gesamt"
            rowKey={(r) => r.category}
            defaultSortBy="category"
            defaultOrder="asc"
          />
        </Section>
      )}

      {data.bySignupLead && data.bySignupLead.length > 0 && (
        <Section
          title="Nach Anmeldezeitpunkt"
          text="Wie lange vor (oder nach) dem Webinar-Start sich die Personen angemeldet haben — erste Anmeldung je Person. Show-up ist nur für Anmeldungen vor dem Start berechenbar (–). Anfos ohne Anmeldung stehen in eigenen Zeilen am Ende. Webinare vor Mai 2026 stehen gesammelt unter „Anmeldezeit unsicher“: dort ist die gespeicherte Uhrzeit nicht sicher einer Zeitzone zuzuordnen."
        >
          <MetricsTable
            rows={[...data.bySignupLead].sort(
              (a, b) => leadRank(a.category) - leadRank(b.category),
            )}
            leading={[
              {
                ...categoryColumn('Anmeldung'),
                sortValue: (r) => leadRank(r.category),
              },
            ]}
            metrics={CHANNEL_METRICS}
            totals={all}
            rowKey={(r) => r.category}
            defaultSortBy="category"
            defaultOrder="asc"
          />
        </Section>
      )}

      <Section title="Nach Affiliate" text="Kleine Affiliates sind zu „Sonstige“ zusammengefasst.">
        <MetricsTable
          rows={data.byAffiliate}
          leading={[categoryColumn('Affiliate')]}
          metrics={CHANNEL_METRICS}
          totals={all}
          rowKey={(r) => r.category}
          defaultSortBy="registrations"
          maxHeight={520}
        />
      </Section>

      <Section title="Nach Gerät">
        <DeviceFunnel
          signup={data.bySignupDevice}
          attendance={data.byAttendanceDevice}
          totals={all}
        />
      </Section>

      <Section title="Kreuz-Analyse: Ad-Type × Blog">
        <MetricsTable
          rows={data.matrix}
          leading={matrixLeading}
          metrics={CHANNEL_METRICS}
          totals={all}
          rowKey={(r) => `${r.ad_type}|${r.blog}`}
          defaultSortBy="registrations"
          maxHeight={520}
        />
      </Section>
    </Box>
  );
};

export default GroupedSections;
